import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: object | object[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  jsonLd,
}: SEOHeadProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const defaultImage = `${siteUrl}/og-image.jpg`;

  useEffect(() => {
    // Set title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)?.[1] || '');
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Set basic meta tags
    setMetaTag('meta[name="description"]', 'content', description);
    if (keywords) {
      setMetaTag('meta[name="keywords"]', 'content', keywords);
    }

    // Set canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = fullCanonical;

    // Set Open Graph tags
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', fullCanonical);
    setMetaTag('meta[property="og:type"]', 'content', ogType);
    setMetaTag('meta[property="og:image"]', 'content', ogImage || defaultImage);

    // Set Twitter Card tags
    setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'content', title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);
    setMetaTag('meta[name="twitter:image"]', 'content', ogImage || defaultImage);

    // Set last-modified
    const lastModified = new Date().toISOString();
    setMetaTag('meta[name="last-modified"]', 'content', lastModified);

    // Handle JSON-LD
    let scriptElement = document.querySelector('script[type="application/ld+json"]#seo-jsonld');
    if (jsonLd) {
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('id', 'seo-jsonld');
        document.head.appendChild(scriptElement);
      }
      const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      scriptElement.textContent = JSON.stringify(jsonLdArray);
    } else if (scriptElement) {
      scriptElement.remove();
    }

    // Cleanup function
    return () => {
      // Keep meta tags for SPA navigation
    };
  }, [title, description, keywords, fullCanonical, ogImage, ogType, jsonLd, siteUrl]);

  return null;
}